import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider } from '@mui/material';
import HelpSidebar from './HelpSidebar';
import { documentUpload } from '../utils/userFacingCopy';

interface IdpHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const IdpHelpSidebar: React.FC<IdpHelpSidebarProps> = ({ isExpanded, onToggle }) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="IDP Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      This page lets you create, view, and manage your <strong>Individual Development Plan (IDP)</strong> for a given year. Your manager approves your IDP and can add quarterly reviews.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      WHAT YOU CAN DO
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Create an IDP"
          secondary="If you don't have an IDP for the year, use Create to start one. You'll set career aspirations and add development goals."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Development goals"
          secondary="Add goals with skill/competency, resources and activities, and target deadline. Target deadlines cannot be weekends or public holidays (based on your organisation's country setting). You can edit or remove goals while the IDP is in Draft."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Submit"
          secondary="When ready, submit your IDP. Your manager will review and approve it. You can't edit goals after submission until the manager approves (or you withdraw)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Quarterly reviews"
          secondary="Once approved, you and your manager can add quarterly review comments. Adding review comments reverts the IDP to Draft so the manager must re-approve (audit trail). Both manager and employee sections must be completed for a quarter to be marked complete."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Supporting documents"
          secondary={`Add evidence, certificates, or notes at any time. Upload files (${documentUpload.allowedTypesShortLabel}). Maximum ${documentUpload.maxFileSizeLabel} per file. Use the Description column to add context. You can view, download, edit filename or description, or delete documents. Uploads, edits, and deletions are recorded in the audit trail.`}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Print or Save as PDF"
          secondary="Use Print on the IDP detail page to open your browser print or save-as-PDF dialog. The printout includes employee details, career aspirations, development goals, status, and the audit trail. Supporting documents are not included."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      IF YOU'RE A MANAGER
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Team IDPs"
          secondary="Use the team or pending-approvals view to see your direct reports' IDPs. The list page header shows how many IDPs are listed (e.g. '5 IDPs'). Open an IDP to approve it or add quarterly reviews."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Approve"
          secondary="After the employee submits, you can approve the IDP. Once approved, they can continue with their development goals and you can add quarterly reviews."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Audit trail"
          secondary="The Audit Trail records IDP created, submitted, approved, quarterly review, and reverted-to-draft (when review comments are added) events. Event times follow Times on screen on your Profile (organisation time zone by default)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      STATUSES
    </Typography>
    <List dense>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Draft"
          secondary="You can edit goals and career aspirations. Submit when ready."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="With manager"
          secondary="You’ve submitted your plan; your manager will acknowledge and support you."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Acknowledged"
          secondary="Your manager has acknowledged your plan. You and your manager can add quarterly reviews."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>
  </HelpSidebar>
);

export default IdpHelpSidebar;
